import React, { useEffect, useState } from "react";
import { sa_ticketColumn } from "Constants/TableColumns";
import Translator from "Languages/Utility/Translator";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import { read } from "Helpers/LocalStorage";
import StatusHandler from "Helpers/StatusHandler";
import TicketingService from "Services/http/endpoints/superadmin/Ticketing";
import { useDispatch, useSelector } from "react-redux";
import { GETAGAINSATICKET } from "Stores/actionTypes";
import addNumToList from "Helpers/addNumToList";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import { Grid, useTheme } from "@material-ui/core";
import { getAccountsAllGroup } from "Stores/actions";
import DatePickerComponent from "Components/DatePickerComponent/DatePickerComponent";
import { useTranslation } from "react-i18next";
import MuiButton from "Components/MuiButton/MuiButton";
import Input from "Components/Input/Input";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import * as Icons from "react-bootstrap-icons";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import { getLocaleDate } from "Helpers/GetLocaleDate";

export default function TblTickets() {
  const { t } = useTranslation();
  const { direction } = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [sizePerPage, setSizePerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState([]);
  const [groupId, setGroupId] = useState(-1);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const { getAllSATicketAgain, allGroups } = useSelector((state) => ({
    getAllSATicketAgain: state.ticketing.getAllSATicketAgain,
    allGroups: state.saAccounts.allGroups,
  }));
  const [controls, setControls] = useSetState({
    title: {
      type: "text",
      row: 1,
      value: "",
      validation: null,
    },
    groups: {
      type: "select",
      row: 1,
      selectItems: [],
      value: "",
      validation: null,
    },
    status: {
      type: "select",
      row: 1,
      selectItems: [],
      value: "",
      validation: null,
    },
    fromDate: {
      type: "date",
      row: 2,
      value: "",
      validation: null,
    },
    toDate: {
      type: "date",
      row: 2,
      value: "",
      validation: null,
    },
  });

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
  };

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    if (controlName === "groups") setGroupId(value.value);
  };

  const handleFromDate = (value) => {
    setFromDate(value);
  };

  const handleToDate = (value) => {
    setToDate(value);
  };

  const handleRemoveDate = () => {
    setFromDate(null);
    setToDate(null);
  };

  const handleSearch = async () => {
    let group = null;
    let date = null;
    let title = null;
    let status = null;
    setLoading(true);
    if (groupId !== -1 && groupId !== 0) group = { GroupId: groupId };
    if (fromDate && toDate)
      date = {
        Date: `${getLocaleDate(fromDate, direction)}-${getLocaleDate(toDate, direction)}`,
      };
    if (controls.title.value !== "") title = { Tittle: controls.title.value };
    if (controls.status.value && controls.status.value.value !== -1)
      status = { StatusId: controls.status.value.value };
    const params = {
      Page: 1,
      ...group,
      ...date,
      ...title,
      ...status,
    };
    setCurrentPage(1);
    await getData(params);
    setLoading(false);
  };

  useEffect(() => {
    onClear();
    onEnter();
  }, []);

  const onClear = async () => {
    setGroupId(-1);
    setFromDate(null);
    setToDate(null);
    setTblData([]);
  };

  const onEnter = async () => {
    setLoading(true);
    await getAllGroups();
    await getFirstData();
    await getTicketsStatus();
    setLoading(false);
  };

  const getAllGroups = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getAccountsAllGroup(params));
      resolve(true);
    });
  };

  const getTicketsStatus = async () => {
    return new Promise(async (resolve) => {
      const response = await TicketingService.getTicketsStatus({
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      });
      const data = response.data.Value;
      let selectItems = [];
      const _selectItems = data.map((item) => {
        return {
          label: item.Name,
          value: item.Id,
        };
      });
      selectItems = selectItems.concat(_selectItems);
      setControls({ status: { selectItems } });
      setControls({
        status: { value: _selectItems.find((i) => i.value === 2) },
      });
      resolve(true);
    });
  };

  useEffect(() => {
    if (allGroups) {
      const selectItems = allGroups.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ groups: { selectItems } });
      setControls({ groups: { value: selectItems[0] } });
      setGroupId(selectItems[0].value);
    }
  }, [allGroups]);

  const getFirstData = async () => {
    return new Promise(async (resolve) => {
      const params = {
        StatusId: 2,
      };
      await getData(params);
      resolve(true);
    });
  };

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      const params = {
        Limit: sizePerPage,
        Page: page,
        StatusId: controls.status.value.value,
      };
      await getData(params);
      setCurrentPage(page);
      setSizePerPage(sizePerPage);
    }
  };

  const getData = async (params) => {
    try {
      setLoading(true);
      const response = await TicketingService.getAdminTickets({
        ...params,
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        Limit: params.Limit ? params.Limit : sizePerPage,
        Page: params.Page ? params.Page : currentPage,
      });
      const err = StatusHandler(response);
      if (!err.isFail) {
        setTblData(
          addNumToList(
            response.data.Value,
            response.data.Pagination.Page,
            response.data.Pagination.Limit
          )
        );
        setCurrentPage(response.data.Pagination.Page);
        setSizePerPage(response.data.Pagination.Limit);
        setTotal(response.data.Pagination.Total);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (getAllSATicketAgain) {
        let group = null;
        let date = null;
        let title = null;
        let status = null;
        setLoading(true);
        if (groupId !== -1 && groupId !== 0) group = { GroupId: groupId };
        if (fromDate && toDate)
          date = {
            Date: `${getLocaleDate(fromDate, direction)}-${getLocaleDate(toDate, direction)}`,
          };
        if (controls.title.value !== "")
          title = { Tittle: controls.title.value };
        if (controls.status.value && controls.status.value.value !== -1)
          status = { StatusId: controls.status.value.value };
        const params = {
          Page: 1,
          ...group,
          ...date,
          ...title,
          ...status,
        };
        await getData(params);
        dispatch({ type: GETAGAINSATICKET, data: false });
        setLoading(false);
      }
    })();
  }, [getAllSATicketAgain]);

  return (
    <>
      <BackDropLoading loading={loading} />
      <MuiPaper>
        <Grid container spacing={2}>
          {Object.keys(controls).map((key) => {
            return (
              <Grid item lg={2} sm={4} xs={12} key={key}>
                {controls[key].type === "text" && (
                  <Input
                    setValue={setValue.bind(this)}
                    value={controls[key].value}
                    name={key}
                    label={<Translator id={key} />}
                    placeholder={t(key)}
                  />
                )}
                {controls[key].type === "select" && (
                  <MuiAutoComplete
                    autoSelect={true}
                    options={controls[key].selectItems}
                    label={<Translator id={key} />}
                    value={controls[key].value}
                    onChange={autoCompleteHandler}
                    id={key}
                  />
                )}
                {key === "fromDate" && (
                  <DatePickerComponent
                    label={t(key)}
                    value={fromDate}
                    onChange={handleFromDate}
                  />
                )}
                {key === "toDate" && (
                  <DatePickerComponent
                    label={t(key)}
                    value={toDate}
                    onChange={handleToDate}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
        <Grid container spacing={2} className="d-flex flex-row-reverse mt-1">
          <Grid item sm={2} xs={12}>
            <MuiButton
              color="secondary"
              title={<Translator id="doFilter" />}
              innerIcon={<Icons.Funnel />}
              onClick={handleSearch}
              disabled={loading}
              className="w-100"
            />
          </Grid>
          {(fromDate || toDate) && (
            <Grid item sm={2} xs={12}>
              <MuiButton
                color="secondary"
                title={"حذف تاریخ"}
                innerIcon={<Icons.CalendarX />}
                onClick={handleRemoveDate}
                disabled={loading}
                className="MuiButton-danger-border w-100"
              />
            </Grid>
          )}
        </Grid>
      </MuiPaper>
      <MuiPaper className="mt-3">
        <RemoteTable
          data={tblData}
          columns={sa_ticketColumn}
          page={currentPage}
          sizePerPage={sizePerPage}
          totalSize={total}
          onTableChange={onTableChange}
        />
      </MuiPaper>
    </>
  );
}
